import { Button, Checkbox, Input } from '@invent/wl-ui-kit-next/ui';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import {
  ChoiceSelect,
  DataTable,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelpPopover,
  StatusChip,
  type DataTableState,
  type IDataColumn
} from '@/design';
import { ApiError } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import { internalFieldsApi } from '../api/internal-fields-api';
import type { INoteInitials, INoteInitialsBody } from '../api/internal-field-contracts';
import { AdminPage, LoadError, STAFF_FIELD_TABS, useAdminData } from '../components/admin-page';
import { formatDay } from '../components/days';
import { userPath } from '../paths';
import { refusalMessage } from '../refusal';

/**
 * The note initials register (SR-494) — who the initials in a survey's notes log stand for, by
 * period, since a tag changed hands over the years. A work page's light form: the header with its
 * "?", a search and *Add initials*, the table; no cards, signals or pager — the register is a few
 * dozen rows, read whole. Admin alone (`internal_fields.author`, the staff fields' tab it sits in).
 *
 * **Nothing is deleted: a period is closed** by its *Until* day, so the entries written under it
 * keep their name. A change is the next read's everywhere — the tags' hover and the initials
 * survey-api signs a new entry with (`internalFieldsApi`'s register read is dropped on a write).
 */
export function AdminNoteInitialsPage() {
  const [search, setSearch] = React.useState('');
  const [editing, setEditing] = React.useState<Nullable<INoteInitials> | 'new'>(null);
  // Read afresh, not the page load's shared copy: this is where the register changes.
  const { data, error, reload } = useAdminData(() => internalFieldsApi.noteInitials(true), []);

  const needle = search.trim().toLowerCase();
  const rows =
    data?.filter(
      (row) =>
        row.initials.toLowerCase().includes(needle) ||
        (row.name ?? '').toLowerCase().includes(needle) ||
        (row.email ?? '').toLowerCase().includes(needle)
    ) ?? null;
  const state: DataTableState | undefined =
    data === null
      ? error
        ? { kind: 'error', content: error }
        : { kind: 'loading' }
      : rows?.length === 0
        ? { kind: 'empty', content: 'No initials match that.' }
        : undefined;

  return (
    <AdminPage
      title="Staff fields"
      tabs={STAFF_FIELD_TABS}
      documentTitle="Note initials"
      actions={
        <HelpPopover label="About note initials" align="end">
          <p>
            A notes log signs each entry with initials — “9/18/26 DF - …”. This register says whose they
            are, so a tag names its person on hover, and gives a new entry its author’s initials.
          </p>
          <p>
            A tag can belong to different people in different years: each row is one person’s period.
            An entry dated outside every period goes to the nearest one. A row with no name marks a
            period nobody could be named for — its tags stay plain.
          </p>
          <p>
            Nothing is deleted: set <em>Until</em> to close a period. <em>Left</em> marks somebody no
            longer at Shook; <em>Low</em> confidence is said as “possibly”.
          </p>
        </HelpPopover>
      }
    >
      {data !== null && error && <LoadError message={error} />}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          type="search"
          placeholder="Initials, name or email…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="max-w-xs"
          aria-label="Search the register"
        />
        <span className="text-sm text-muted-foreground">
          {rows?.length ?? 0} of {data?.length ?? 0}
        </span>
        <Button className="ml-auto" onClick={() => setEditing('new')}>
          <Plus />
          Add initials
        </Button>
      </div>
      <DataTable
        label="Note initials"
        columns={columns((row) => setEditing(row), overlapsOf(data ?? []))}
        rows={rows}
        rowKey={(row) => row.id}
        onOpen={(row) => setEditing(row)}
        state={state}
        maxHeight="none"
      />
      {editing !== null && (
        <InitialsDialog
          row={editing === 'new' ? null : editing}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
          onClose={() => setEditing(null)}
        />
      )}
    </AdminPage>
  );
}

const CONFIDENCE_WORDS: Readonly<Record<string, string>> = { high: 'High', medium: 'Medium', low: 'Low' };

function period(row: INoteInitials): string {
  if (row.validFrom === null && row.validTo === null) return 'Any date';
  if (row.validTo === null) return `From ${formatDay(row.validFrom!)}`;
  if (row.validFrom === null) return `Until ${formatDay(row.validTo)}`;
  return `${formatDay(row.validFrom)} – ${formatDay(row.validTo)}`;
}

/** One person's period of a tag, as days to compare — open ends far out. */
function span(row: INoteInitials): [string, string] {
  return [row.validFrom ?? '0000-01-01', row.validTo ?? '9999-12-31'];
}

/** The same person in two rows — Blake's B and BP, Caryn's CG and CSG — is not a clash. */
function samePerson(a: INoteInitials, b: INoteInitials): boolean {
  if (a.email !== null && b.email !== null) return a.email.toLowerCase() === b.email.toLowerCase();
  return a.name !== null && a.name === b.name;
}

/**
 * The rows whose period of a tag overlaps another person's period of the same tag, each with the
 * other rows' names (SR-494): saved all the same — the register is the research's, and a period
 * being settled may overlap for a while — but said on the row, since a tag on a day in both periods
 * names whichever row is read first.
 */
function overlapsOf(rows: readonly INoteInitials[]): ReadonlyMap<string, string[]> {
  const found = new Map<string, string[]>();
  for (const row of rows) {
    const [from, to] = span(row);
    const others = rows.filter((other) => {
      if (other.id === row.id || other.initials !== row.initials || samePerson(row, other)) return false;
      const [otherFrom, otherTo] = span(other);
      return from <= otherTo && otherFrom <= to;
    });
    if (others.length > 0) found.set(row.id, others.map((other) => other.name ?? 'Not identified'));
  }
  return found;
}

function columns(
  onEdit: (row: INoteInitials) => void,
  overlaps: ReadonlyMap<string, string[]>
): IDataColumn<INoteInitials>[] {
  return [
    {
      id: 'initials',
      header: 'Initials',
      cell: (row) => <span className="font-medium">{row.initials}</span>
    },
    {
      id: 'person',
      header: 'Person',
      cell: (row) => row.name ?? <span className="text-muted-foreground">Not identified</span>
    },
    { id: 'email', header: 'Email', cell: (row) => <span className="break-all">{row.email ?? '—'}</span> },
    {
      id: 'period',
      header: 'Period',
      cell: (row) => {
        const clash = overlaps.get(row.id);
        return (
          <span className="grid gap-0.5">
            {period(row)}
            {clash && (
              <span data-overlap="" className="text-xs text-destructive">
                Overlaps {clash.join(', ')}
              </span>
            )}
          </span>
        );
      }
    },
    {
      id: 'status',
      header: 'Status',
      cell: (row) =>
        row.name === null ? null : row.left ? <StatusChip tone="neutral">Left</StatusChip> : <StatusChip tone="success">At Shook</StatusChip>
    },
    {
      id: 'confidence',
      header: 'Confidence',
      cell: (row) => (row.confidence ? CONFIDENCE_WORDS[row.confidence] : '—')
    },
    {
      id: 'account',
      header: 'Account',
      // The staff account the address belongs to, linked (the ticket's "where a staff account
      // exists, link it"): its user card, where its roles and sign-ins are.
      cell: (row) =>
        row.accountId && row.email ? (
          <Link
            to={userPath(row.email)}
            onClick={(event) => event.stopPropagation()}
            className="font-medium text-[var(--color-sapphire)] hover:underline"
          >
            Open
          </Link>
        ) : (
          <span className="text-muted-foreground">None</span>
        )
    },
    {
      id: 'changed',
      header: 'Changed by / when',
      cell: (row) =>
        row.updatedBy === null ? (
          <span className="text-muted-foreground">Seeded · {formatDateTime(row.updatedAt)}</span>
        ) : (
          <span className="grid">
            <span className="break-all">{row.updatedBy}</span>
            <span className="text-xs text-muted-foreground">{formatDateTime(row.updatedAt)}</span>
          </span>
        )
    },
    {
      id: 'edit',
      header: <span className="sr-only">Edit</span>,
      label: 'Edit',
      cell: (row) => (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0"
          aria-label={`Edit ${row.initials}${row.name ? ` — ${row.name}` : ''}`}
          onClick={(event) => {
            event.stopPropagation();
            onEdit(row);
          }}
        >
          Edit
        </Button>
      )
    }
  ];
}

const NO_CONFIDENCE = 'none';

/** survey-api's members as the form's labels name them — a refusal reads "Until is on or after
 *  validFrom." under its field rather than as a wire member's fragment. */
const FIELD_WORDS: Readonly<Record<string, string>> = {
  initials: 'Initials',
  confidence: 'Confidence',
  name: 'Person',
  email: 'Email',
  validFrom: 'From',
  validTo: 'Until'
};

/** One register row's form — a new row, or a row's whole edit; a period closed by *Until*. */
function InitialsDialog({
  row,
  onSaved,
  onClose
}: {
  row: Nullable<INoteInitials>;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [body, setBody] = React.useState<INoteInitialsBody>(() => ({
    initials: row?.initials ?? '',
    validFrom: row?.validFrom ?? null,
    validTo: row?.validTo ?? null,
    name: row?.name ?? null,
    email: row?.email ?? null,
    left: row?.left ?? false,
    confidence: row ? row.confidence : 'high'
  }));
  const [problems, setProblems] = React.useState<ReadonlyMap<string, string>>(new Map());
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const formId = React.useId();
  const set = <K extends keyof INoteInitialsBody>(key: K, value: INoteInitialsBody[K]) =>
    setBody((now) => ({ ...now, [key]: value }));
  const blank = (value: Nullable<string>) => (value === null || value.trim() === '' ? null : value.trim());

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setSaid(null);
    setProblems(new Map());
    try {
      const sent = { ...body, initials: body.initials.trim(), name: blank(body.name), email: blank(body.email) };
      if (row) await internalFieldsApi.updateNoteInitials(row.id, sent);
      else await internalFieldsApi.createNoteInitials(sent);
      onSaved();
    } catch (error) {
      setBusy(false);
      const listed = error instanceof ApiError ? (error.problem?.problems ?? []) : [];
      // Each said under its field in that field's words; one the form has no field for, above
      // the buttons — never a refusal with nothing on the screen.
      const placed = listed.filter((one) => one.target in FIELD_WORDS);
      const rest = listed.filter((one) => !(one.target in FIELD_WORDS));
      setProblems(new Map(placed.map((one) => [one.target, `${FIELD_WORDS[one.target]} ${one.reason}`])));
      if (rest.length > 0) setSaid(rest.map((one) => `${one.target} ${one.reason}`).join(' '));
      else if (placed.length === 0) setSaid(refusalMessage(error, 'The initials could not be saved.'));
    }
  };

  const field = (key: string, label: string, control: React.ReactElement<Record<string, unknown>>) => {
    const problem = problems.get(key);
    const problemId = `${formId}-${key}`;
    return (
      <label className="grid gap-1">
        <span className="font-medium">{label}</span>
        {problem
          ? React.cloneElement(control, { 'aria-invalid': true, 'aria-describedby': problemId })
          : control}
        {problem && (
          <span id={problemId} role="alert" className="text-xs text-destructive">
            {problem}
          </span>
        )}
      </label>
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent>
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle>{row ? `Edit ${row.initials}` : 'Add initials'}</DialogTitle>
            <DialogDescription>
              {row ? 'Close a period with Until rather than removing it.' : 'One person’s period of one tag.'}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-3 text-sm sm:grid-cols-2">
            {field(
              'initials',
              'Initials',
              <Input
                required
                value={body.initials}
                maxLength={3}
                onChange={(event) => set('initials', event.target.value)}
              />
            )}
            {field(
              'confidence',
              'Confidence',
              <ChoiceSelect
                aria-label="Confidence"
                value={body.confidence ?? NO_CONFIDENCE}
                onChange={(next) =>
                  set('confidence', next === NO_CONFIDENCE ? null : (next as INoteInitialsBody['confidence']))
                }
                choices={[
                  { value: 'high', label: 'High' },
                  { value: 'medium', label: 'Medium' },
                  { value: 'low', label: 'Low — said as “possibly”' },
                  { value: NO_CONFIDENCE, label: 'Not set' }
                ]}
              />
            )}
            {field(
              'name',
              'Person',
              <Input
                value={body.name ?? ''}
                placeholder="Empty: nobody could be named"
                onChange={(event) => set('name', event.target.value)}
              />
            )}
            {field(
              'email',
              'Email',
              <Input type="email" value={body.email ?? ''} onChange={(event) => set('email', event.target.value)} />
            )}
            {field(
              'validFrom',
              'From',
              <Input
                type="date"
                value={body.validFrom ?? ''}
                onChange={(event) => set('validFrom', event.target.value || null)}
              />
            )}
            {field(
              'validTo',
              'Until',
              <Input
                type="date"
                value={body.validTo ?? ''}
                onChange={(event) => set('validTo', event.target.value || null)}
              />
            )}
            <label className="flex items-center gap-2 sm:col-span-2">
              <Checkbox checked={body.left} onCheckedChange={(checked) => set('left', checked === true)} />
              Left Shook — said as “(left)”
            </label>
            {said && (
              <p role="alert" className="text-destructive sm:col-span-2">
                {said}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || body.initials.trim() === ''}>
              {busy ? 'Saving…' : row ? 'Save' : 'Add'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
